import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Sparkles, RefreshCw, GitBranch, Navigation, BarChart3 } from 'lucide-react';
import { Button } from '../Button';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../utils/constants';

// Hook to detect reduced motion preference
function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return prefersReducedMotion;
}

// Hook to detect mobile viewport
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 640;
  });

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return isMobile;
}

export function Hero() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const heroRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobile = useIsMobile();

  // Create persistent QR pattern with alignment squares
  const qrPattern = useMemo<boolean[]>(() => {
    const pattern = Array.from({ length: 64 }).map(() => Math.random() > 0.4);

    // Set alignment squares (single squares: top-left: 0; top-right: 7; bottom-left: 56)
    const alignmentPositions = [0, 7, 56];
    alignmentPositions.forEach((pos) => {
      pattern[pos] = true;
    });

    return pattern;
  }, []);

  const [floatOffsets, setFloatOffsets] = useState({
    analytics: { x: 0, y: 0, rotation: 0, scale: 1 },
    dynamic: { x: 0, y: 0, rotation: 0, scale: 1 },
    smartRouting: { x: 0, y: 0, rotation: 0, scale: 1 },
    abTesting: { x: 0, y: 0, rotation: 0, scale: 1 },
  });

  const mousePositionRef = useRef({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState<string | null>(null);
  const [isAnyCardHovered, setIsAnyCardHovered] = useState(false);

  const [metrics, setMetrics] = useState({
    dynamic: 'Real-time URL Updates',
    abTesting: 'Performance Optimization',
    smartRouting: 'Context-Aware Delivery',
    analytics: 'Deep Performance Insights',
  });


  // Randomization every 6 seconds with feature-focused metrics (paused on hover)
  useEffect(() => {
    const dynamicOptions = ['Real-time URL Updates', 'Live Content Changes', 'Dynamic Redirects', 'Instant URL Swaps'];
    const abTestingOptions = ['Performance Optimization', 'A/B Testing Active', 'Conversion Testing', 'Split Testing'];
    const routingOptions = ['Context-Aware Delivery', 'Smart Redirection', 'Intelligent Routing', 'Adaptive Delivery'];
    const analyticsOptions = ['Deep Performance Insights', 'Advanced Analytics', 'Detailed Reporting', 'Comprehensive Tracking'];

    const interval = setInterval(() => {
      // Only update metrics if no card is being hovered
      if (!isAnyCardHovered) {
        setMetrics({
          dynamic: dynamicOptions[Math.floor(Math.random() * dynamicOptions.length)],
          abTesting: abTestingOptions[Math.floor(Math.random() * abTestingOptions.length)],
          smartRouting: routingOptions[Math.floor(Math.random() * routingOptions.length)],
          analytics: analyticsOptions[Math.floor(Math.random() * analyticsOptions.length)],
        });
      }
    }, 6000);

    return () => clearInterval(interval);
  }, [isAnyCardHovered]);

  // Mouse tracking for interactive effects. Stored in a ref (not state) so
  // mousemove doesn't re-render or restart the floating-animation effect below.
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        mousePositionRef.current = {
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height
        };
      }
    };

    const heroElement = heroRef.current;
    if (heroElement) {
      heroElement.addEventListener('mousemove', handleMouseMove);
      return () => heroElement.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  // Enhanced floating animation with staggered, organic movement
  // Disabled on mobile and when reduced motion is preferred
  useEffect(() => {
    // Skip animations if reduced motion is preferred or on mobile
    if (prefersReducedMotion || isMobile) {
      setFloatOffsets({
        analytics: { x: 0, y: 0, rotation: 0, scale: 1 },
        dynamic: { x: 0, y: 0, rotation: 0, scale: 1 },
        smartRouting: { x: 0, y: 0, rotation: 0, scale: 1 },
        abTesting: { x: 0, y: 0, rotation: 0, scale: 1 },
      });
      return;
    }

    let animationId: number;
    let startTime = Date.now();

    const animate = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const mousePosition = mousePositionRef.current;

      setFloatOffsets({
        analytics: {
          x: Math.sin(elapsed * 0.5 + 0) * 12 + (mousePosition.x - 0.5) * 8,
          y: Math.cos(elapsed * 0.3 + 0) * 8 + (mousePosition.y - 0.5) * 6,
          rotation: Math.sin(elapsed * 0.2) * 2,
          scale: 1 + Math.sin(elapsed * 0.4) * 0.02,
        },
        dynamic: {
          x: Math.sin(elapsed * 0.4 + Math.PI / 4) * 15 + (mousePosition.x - 0.5) * 10,
          y: Math.cos(elapsed * 0.6 + Math.PI / 4) * 10 + (mousePosition.y - 0.5) * 8,
          rotation: Math.sin(elapsed * 0.3 + Math.PI / 4) * 3,
          scale: 1 + Math.cos(elapsed * 0.5) * 0.03,
        },
        smartRouting: {
          x: Math.sin(elapsed * 0.6 + Math.PI / 2) * 10 + (mousePosition.x - 0.5) * 6,
          y: Math.cos(elapsed * 0.4 + Math.PI / 2) * 12 + (mousePosition.y - 0.5) * 9,
          rotation: Math.sin(elapsed * 0.25 + Math.PI / 2) * 2.5,
          scale: 1 + Math.sin(elapsed * 0.35) * 0.025,
        },
        abTesting: {
          x: Math.sin(elapsed * 0.35 + Math.PI) * 14 + (mousePosition.x - 0.5) * 7,
          y: Math.cos(elapsed * 0.5 + Math.PI) * 9 + (mousePosition.y - 0.5) * 5,
          rotation: Math.sin(elapsed * 0.4 + Math.PI) * 1.8,
          scale: 1 + Math.cos(elapsed * 0.3) * 0.02,
        },
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationId);
  }, [prefersReducedMotion, isMobile]);

  // Helper function to render alignment squares
  const renderAlignmentSquare = () => (
    <div className="w-full h-full relative bg-[--clinqr-navy] rounded-sm flex items-center justify-center">
      <div className="w-3/4 h-3/4 bg-white/80 rounded-sm flex items-center justify-center">
        <div className="w-1/2 h-1/2 bg-[--clinqr-navy] rounded-sm"></div>
      </div>
    </div>
  );

  // Check if position is an alignment square
  const isAlignmentSquare = (index: number) => {
    const alignmentPositions = [0, 7, 56]; // top-left, top-right, bottom-left
    return alignmentPositions.includes(index);
  };

  const handlePrimaryButton = () => {
    if (isAuthenticated) {
      navigate(ROUTES.DASHBOARD);
    } else {
      navigate('/register');
    }
  };

  // Function to scroll to Features section with optional feature ID
  const scrollToFeatures = (featureId?: string) => {
    const featuresSection = document.querySelector('[data-section="features"]');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' });
      // Update URL hash if featureId is provided
      if (featureId) {
        // Use setTimeout to ensure scroll happens before hash change
        setTimeout(() => {
          window.location.hash = featureId;
        }, 100);
      }
    } else {
      // If we're not on the landing page, navigate to it with hash
      if (featureId) {
        navigate(`/#${featureId}`);
      } else {
        navigate('/');
      }
    }
  };

  // Function to scroll to Problem section
  const scrollToProblem = () => {
    const problemSection = document.querySelector('[data-section="problem-solution"]');
    if (problemSection) {
      problemSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle card hover events
  const handleCardHover = (cardId: string, isHovering: boolean) => {
    setIsHovering(isHovering ? cardId : null);
    setIsAnyCardHovered(isHovering);
  };

  return (
    <section ref={heroRef} className="bg-[--clinqr-bg-subtle] relative overflow-hidden min-h-screen flex flex-col justify-center">
      {/* Bold geometric shape instead of blur */}
      <div className="absolute top-0 right-0 w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] lg:w-[500px] lg:h-[500px] bg-[--clinqr-navy] opacity-[0.02] rounded-[40px] -rotate-12 translate-x-1/4 -translate-y-1/4"></div>

      {/* Subtle dot pattern for texture */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, var(--clinqr-navy) 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
        }}
      />

      <div className="container-page py-12 sm:py-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-[55%_45%] gap-8 lg:gap-16 items-center">
          {/* Left Column - Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[--clinqr-lime] text-[--clinqr-navy] mb-6 sm:mb-8 shadow-[0_4px_20px_rgba(212,255,128,0.3)] border border-[--clinqr-navy]/5">
              <Sparkles className="w-4 h-4 text-[--clinqr-navy] fill-[--clinqr-navy]" strokeWidth={2} />
              <span className="text-sm font-bold tracking-tight">A/B Testing for QR codes</span>
            </div>

            {/* H1 Headline - Using display font */}
            <h1 className="heading-display mb-6 sm:mb-8 text-[--clinqr-navy]">
              Smart QR codes that <span className="text-[--clinqr-navy] italic relative inline-block px-1">
                adapt
                <span className="absolute bottom-1 left-0 w-full h-[30%] bg-[--clinqr-lime] -z-10 rotate-1 opacity-80"></span>
              </span> to your audience
            </h1>

            {/* Subheading */}
            <p className="body-lg mb-6 sm:mb-8 max-w-2xl mx-auto lg:mx-0 text-[--clinqr-navy]/70 font-medium">
              Create dynamic QR codes that route users based on location, device, time, and more. Track every scan with powerful analytics.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6 mb-4">
              <Button
                variant="primary"
                className="px-8 sm:px-10 h-14 w-full sm:w-auto text-lg shadow-2xl transition-transform"
                onClick={handlePrimaryButton}
              >
                {isAuthenticated ? 'Go to Dashboard' : 'Start for Free'}
              </Button>
              <button
                type="button"
                className="px-8 sm:px-10 h-14 w-full sm:w-auto text-lg font-semibold border-2 border-[--clinqr-navy] text-[--clinqr-navy] bg-transparent hover:bg-gray-200 transition-all duration-200 rounded-lg inline-flex items-center justify-center gap-2"
                onClick={scrollToProblem}
              >
                Learn More
              </button>
            </div>

            {/* Small text */}
            <p className="text-sm text-[--clinqr-navy]/40 font-bold">
              No credit card required • 1 free QR code
            </p>
          </div>

          {/* Right Column - Visual - QR Code with Overlay Cards */}
          <div className="relative flex flex-col items-center justify-center order-first lg:order-last">
            {/* QR Code Container - adjusted height for mobile */}
            <div className="relative h-[280px] sm:h-[400px] lg:h-[500px] w-full flex items-center justify-center">
              {/* Center - QR Code (background) */}
              <div className="w-[160px] h-[160px] sm:w-[240px] sm:h-[240px] lg:w-[280px] lg:h-[280px] bg-gray-200/50 rounded-2xl p-3 sm:p-6 shadow-2xl backdrop-blur-sm border border-white/20">
                <div className="w-full h-full bg-black/10 rounded-lg grid grid-cols-8 gap-0.5 sm:gap-1 p-1.5 sm:p-2">
                  {qrPattern.map((isActive, i) => (
                    <div key={i} className="relative">
                      {isAlignmentSquare(i) ? (
                        renderAlignmentSquare()
                      ) : (
                        <div
                          className={`w-full h-full ${isActive ? 'bg-[--clinqr-navy]' : 'bg-transparent'} rounded-sm`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating cards - hidden on mobile (<640px), shown on sm and up */}
              {/* Top-left card - Analytics Dashboard */}
              <div
                className="hidden sm:block absolute top-0 left-0 bg-white rounded-2xl shadow-2xl p-3 sm:p-5 z-10 w-[180px] sm:w-[200px] lg:w-[240px] transition-shadow duration-300 ease-out cursor-pointer hover:shadow-3xl border border-[--clinqr-navy]/5"
                style={{
                  transform: `translate(${floatOffsets.analytics.x}px, ${floatOffsets.analytics.y}px) rotate(${floatOffsets.analytics.rotation}deg) scale(${isHovering === 'analytics' ? 1.05 : floatOffsets.analytics.scale})`,
                }}
                onMouseEnter={() => handleCardHover('analytics', true)}
                onMouseLeave={() => handleCardHover('analytics', false)}
                onTouchStart={() => handleCardHover('analytics', true)}
                onTouchEnd={() => handleCardHover('analytics', false)}
                onClick={() => scrollToFeatures('analytics')}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[--clinqr-navy] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-[--clinqr-lime]" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[--clinqr-navy]/30">Analytics</p>
                    <p className="text-xs font-bold text-[--clinqr-navy] leading-tight mt-0.5">{metrics.analytics}</p>
                  </div>
                </div>
              </div>

              {/* Top-right card - Dynamic QR Codes */}
              <div
                className="hidden sm:block absolute top-0 right-0 bg-white rounded-2xl shadow-2xl p-3 sm:p-5 z-10 w-[180px] sm:w-[200px] lg:w-[240px] transition-shadow duration-300 ease-out cursor-pointer hover:shadow-3xl border border-[--clinqr-navy]/5"
                style={{
                  transform: `translate(${floatOffsets.dynamic.x}px, ${floatOffsets.dynamic.y}px) rotate(${floatOffsets.dynamic.rotation}deg) scale(${isHovering === 'dynamic' ? 1.05 : floatOffsets.dynamic.scale})`,
                }}
                onMouseEnter={() => handleCardHover('dynamic', true)}
                onMouseLeave={() => handleCardHover('dynamic', false)}
                onTouchStart={() => handleCardHover('dynamic', true)}
                onTouchEnd={() => handleCardHover('dynamic', false)}
                onClick={() => scrollToFeatures('dynamic-routing')}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[--clinqr-navy] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 text-[--clinqr-lime]" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[--clinqr-navy]/30">Dynamic</p>
                    <p className="text-xs font-bold text-[--clinqr-navy] leading-tight mt-0.5">{metrics.dynamic}</p>
                  </div>
                </div>
              </div>

              {/* Bottom-left card - Smart Routing */}
              <div
                className="hidden sm:block absolute bottom-0 left-0 bg-white rounded-2xl shadow-2xl p-3 sm:p-5 z-10 w-[180px] sm:w-[200px] lg:w-[240px] transition-shadow duration-300 ease-out cursor-pointer hover:shadow-3xl border border-[--clinqr-navy]/5"
                style={{
                  transform: `translate(${floatOffsets.smartRouting.x}px, ${floatOffsets.smartRouting.y}px) rotate(${floatOffsets.smartRouting.rotation}deg) scale(${isHovering === 'smartRouting' ? 1.05 : floatOffsets.smartRouting.scale})`,
                }}
                onMouseEnter={() => handleCardHover('smartRouting', true)}
                onMouseLeave={() => handleCardHover('smartRouting', false)}
                onTouchStart={() => handleCardHover('smartRouting', true)}
                onTouchEnd={() => handleCardHover('smartRouting', false)}
                onClick={() => scrollToFeatures('location-routing')}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[--clinqr-navy] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Navigation className="w-4 h-4 sm:w-5 sm:h-5 text-[--clinqr-lime]" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[--clinqr-navy]/30">Routing</p>
                    <p className="text-xs font-bold text-[--clinqr-navy] leading-tight mt-0.5">{metrics.smartRouting}</p>
                  </div>
                </div>
              </div>

              {/* Bottom-right card - A/B Testing */}
              <div
                className="hidden sm:block absolute bottom-0 right-0 bg-white rounded-2xl shadow-2xl p-3 sm:p-5 z-10 w-[180px] sm:w-[200px] lg:w-[240px] transition-shadow duration-300 ease-out cursor-pointer hover:shadow-3xl border border-[--clinqr-navy]/5"
                style={{
                  transform: `translate(${floatOffsets.abTesting.x}px, ${floatOffsets.abTesting.y}px) rotate(${floatOffsets.abTesting.rotation}deg) scale(${isHovering === 'abTesting' ? 1.05 : floatOffsets.abTesting.scale})`,
                }}
                onMouseEnter={() => handleCardHover('abTesting', true)}
                onMouseLeave={() => handleCardHover('abTesting', false)}
                onTouchStart={() => handleCardHover('abTesting', true)}
                onTouchEnd={() => handleCardHover('abTesting', false)}
                onClick={() => scrollToFeatures('ab-testing')}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[--clinqr-navy] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <GitBranch className="w-4 h-4 sm:w-5 sm:h-5 text-[--clinqr-lime]" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[--clinqr-navy]/30">A/B Testing</p>
                    <p className="text-xs font-bold text-[--clinqr-navy] leading-tight mt-0.5">{metrics.abTesting}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile-only feature pills - shown only on small screens */}
            <div className="sm:hidden flex flex-wrap justify-center gap-2 mt-4 px-2">
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-lg border border-[--clinqr-navy]/5">
                <div className="w-6 h-6 bg-[--clinqr-navy] rounded flex items-center justify-center shadow-md">
                  <BarChart3 className="w-3.5 h-3.5 text-[--clinqr-lime]" strokeWidth={2} />
                </div>
                <span className="text-xs font-bold text-[--clinqr-navy]">Analytics</span>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-lg border border-[--clinqr-navy]/5">
                <div className="w-6 h-6 bg-[--clinqr-navy] rounded flex items-center justify-center shadow-md">
                  <RefreshCw className="w-3.5 h-3.5 text-[--clinqr-lime]" strokeWidth={2} />
                </div>
                <span className="text-xs font-bold text-[--clinqr-navy]">Dynamic</span>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-lg border border-[--clinqr-navy]/5">
                <div className="w-6 h-6 bg-[--clinqr-navy] rounded flex items-center justify-center shadow-md">
                  <Navigation className="w-3.5 h-3.5 text-[--clinqr-lime]" strokeWidth={2} />
                </div>
                <span className="text-xs font-bold text-[--clinqr-navy]">Routing</span>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-lg border border-[--clinqr-navy]/5">
                <div className="w-6 h-6 bg-[--clinqr-navy] rounded flex items-center justify-center shadow-md">
                  <GitBranch className="w-3.5 h-3.5 text-[--clinqr-lime]" strokeWidth={2} />
                </div>
                <span className="text-xs font-bold text-[--clinqr-navy]">A/B Test</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
